import type { IContainerDashboard, IContainerMiner } from '@/types/rc/containers'
import type {
  IContainerDashboardFilters,
  IInvoiceFilters,
  IMinerDashboardFilters,
  IMinerHistoryFilters,
  IRepairRequestFilters,
  IResourceSearchFilters,
  IRSAFilters,
  IRSAVersionFilters,
  ISparePartHistoryFilters,
  ISparePartsDashboardFilters,
} from '@/types/rc/filters'
import type {
  IAddBulkContainersToShipmentForm,
  IContainerCreationForm,
  IContainerStatusUpdate,
  IContainerUpdateForm,
  ICreateRSAForm,
  ICreateRSAVersionForm,
  IMinerPartUpdateForm,
  ISparePartBulkUpdateForm,
  ISparePartCreationForm,
  ISparePartUpdateForm,
} from '@/types/rc/forms'
import type { IInvoice, IInvoiceCreationForm, IInvoiceDetails, IInvoiceUpdateForm } from '@/types/rc/invoices'
import type {
  BulkMinersResult,
  IComponent,
  IComponentType,
  IDetailsPart,
  IFailure,
  IFailureType,
  IMinerDetails,
  IMinerDetailsLite,
  IMinerHistory,
  IMinerIdentifier,
  IStressTest,
} from '@/types/rc/miners'
import type {
  IMinerDiagnosticForm,
  IMinerPartRepairmentForm,
  IMinerUpdateForm,
  IStressTestForm,
  IUpdateFailureForm,
} from '@/types/rc/miners/forms'
import type { PartType } from '@/types/rc/miners/PartType'
import type {
  CreateRepairRequestForm,
  CustomRepairFeeItem,
  ScopeOfWorkService,
} from '@/types/rc/repair-requests/CreateRepairRequest'
import type { IContainer } from '@/types/rc/shipments'
import type { BulkSparePartsResult, ISparePart, ISparePartHistory } from '@/types/rc/spare-parts'
import type { IPage } from '@/types/shared/pagination'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import axios, { AxiosError } from 'axios'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'

/**
 * Gets a given RC App customer's RSAs.
 * @param customerId a customer ID.
 */
export async function getCustomerRSAs(customer_id: string) {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_RSA_ENDPOINT}`, {
    params: {
      customer_id,
    },
  })
  return res.data
}

/**
 * Fetches a repair request by repair request ID.
 *
 * @param requestId the repair request's ID, e.g. "RR-802305ef9349461eadb92e3e6a7a5d1c"
 */
export async function getRepairRequest(requestId: string) {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_REPAIR_REQUESTS_ENDPOINT}/${requestId}`)
  return res.data
}

/**
 * Gets paginated repair requests by customer ID.
 *
 * @param filters filters (customer ID, page, per page, status, order by field, order by order).
 * @throws {AxiosError}
 */
export async function getRepairRequests(filters: IRepairRequestFilters) {
  const res = await axios.get(import.meta.env.VITE_APP_RC_REPAIR_REQUESTS_ENDPOINT, {
    params: filters,
    paramsSerializer: {
      indexes: null,
    },
  })
  return res.data
}

/**
 * Creates a repair request with "Create Repair Request" form data.
 *
 * @param form a `CreateRepairRequest` object.
 * @returns an object representing the newly-created repair request.
 */
export async function createRepairRequest(form: CreateRepairRequestForm) {
  const formData = new FormData()
  formData.append('customer_id', form.customer_id.toString())
  formData.append('type', form.type)
  formData.append('request_date', form.request_date.toISOString())
  formData.append('expected_units', form.expected_units as unknown as string)
  form.miner_models.forEach((mm) => {
    formData.append('miner_models', mm)
  })
  form.repair_fees.forEach((rf: CustomRepairFeeItem, index) => {
    formData.append(`repair_fees[${index}]repair_action_id`, rf.repair_action_id as string)
    formData.append(`repair_fees[${index}]price`, rf.price as unknown as string)
  })
  form.scope_of_work.forEach((svc: ScopeOfWorkService, index) => {
    formData.append(`scope_of_work[${index}]service_id`, svc.service_id as string)
    formData.append(`scope_of_work[${index}]price`, svc.price as unknown as string)
    svc.included_service_ids?.forEach((included_svc_id: number, nestedIdx) => {
      formData.append(`scope_of_work[${index}]included_service_ids[${nestedIdx}]`, included_svc_id.toString())
    })
  })
  if (form.signed_document?.raw) {
    formData.append('signed_documents', form.signed_document.raw)
  }

  const res = await axios.post(import.meta.env.VITE_APP_RC_REPAIR_REQUESTS_ENDPOINT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

/**
 * Updates a repair request with repair request form data.
 *
 * PATCHes resource with updates values from form,
 * and DELETEs documents marked for deletion (if any).
 *
 * @param form repair request update form data.
 * @param deletionMarkedDocuments documents marked for deletion during repair request update.
 */
export async function updateRepairRequest(
  requestId: string,
  form: CreateRepairRequestForm,
  deletionMarkedDocuments: number[] = []
) {
  // Delete documents marked for deletion
  const baseURL = `${import.meta.env.VITE_APP_RC_REPAIR_REQUESTS_ENDPOINT}/${requestId}/documents`
  for (const documentId of deletionMarkedDocuments) {
    const documentURL = `${baseURL}/${documentId}`
    await axios.delete(documentURL)
  }

  // Update repair request with update form data
  const formData = new FormData()
  formData.append('expected_units', form.expected_units as unknown as string)
  form.miner_models.forEach((mm) => {
    formData.append('miner_models', mm)
  })
  if (form.repair_fees.length > 0) {
    form.repair_fees.forEach((rf: CustomRepairFeeItem, index) => {
      formData.append(`repair_fees[${index}]repair_action_id`, rf.repair_action_id as string)
      formData.append(`repair_fees[${index}]price`, rf.price as unknown as string)
    })
  } else {
    // Delete existing repair fees (send empty string)
    formData.append('repair_fees', '')
  }
  if (form.scope_of_work.length > 0) {
    form.scope_of_work.forEach((svc: ScopeOfWorkService, index) => {
      formData.append(`scope_of_work[${index}]service_id`, svc.service_id as string)
      formData.append(`scope_of_work[${index}]price`, svc.price as unknown as string)
      svc.included_service_ids?.forEach((included_svc_id: number, nestedIdx) => {
        formData.append(`scope_of_work[${index}]included_service_ids[${nestedIdx}]`, included_svc_id.toString())
      })
    })
  } else {
    // Delete existing repair fees (send empty string)
    formData.append('scope_of_work', '')
  }
  if (form.signed_document?.raw) {
    formData.append('signed_documents', form.signed_document.raw)
  }

  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_REPAIR_REQUESTS_ENDPOINT}/${requestId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function updateRepairRequestStatus(repairRequestNumber: string, status: string) {
  const formData = new FormData()
  formData.append('status', status)
  const res = await axios.patch(
    `${import.meta.env.VITE_APP_RC_REPAIR_REQUESTS_ENDPOINT}/${repairRequestNumber}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return res.data
}

/**
 * Retrieves a document for a repair request.
 *
 * @param requestId a repair request ID.
 * @param documentId ID of the repair request's document to retrieve.
 */
export async function getRepairRequestDocument(requestId: string, documentId: number) {
  const res = await axios.get(
    `${import.meta.env.VITE_APP_RC_REPAIR_REQUESTS_ENDPOINT}/${requestId}/documents/${documentId}`,
    { responseType: 'blob' }
  )
  return res.data
}

/**
 * Gets paginated RSAs.
 *
 * @param {IRSAFilters} filters
 * @returns paginated RSA data.
 */
export async function getRSAs(filters?: IRSAFilters): Promise<any> {
  const res = await axios.get(import.meta.env.VITE_APP_RC_RSA_ENDPOINT, {
    params: filters,
  })
  return res.data
}

/**
 * Gets paginated RSA versions.
 *
 * @param {IRSAFilters} filters
 * @returns paginated RSA versions.
 */
export async function getRSAVersions(filters?: IRSAVersionFilters): Promise<any> {
  const res = await axios.get(import.meta.env.VITE_APP_RC_RSA_VERSIONS_ENDPOINT, {
    params: filters,
  })
  return res.data
}

/**
 * Gets an RSA version document.
 *
 * @param rsaVersionNumber RSA document number.
 * @returns {Blob} RSA document blob.
 */
export async function getRSAVersionDocument(rsaVersionNumber: string): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_RSA_VERSIONS_ENDPOINT}/${rsaVersionNumber}/document`, {
    responseType: 'blob',
  })
  return res.data
}

/**
 * Creates a customer RSA.
 *
 * @param {ICreateRSAForm} form
 * @returns created RSA details.
 * @throws {AxiosError}
 */
export async function createRSA(form: ICreateRSAForm) {
  const formData = new FormData()
  formData.append('customer_id', form.customer_id.toString())
  // start_date will always be populated on creation.
  formData.append('start_date', DateTime.fromJSDate(form.start_date!).startOf('day').toISO()!)
  formData.append('months_term', form.months_term.toString())
  if (form.signed_document?.raw) formData.append('signed_document', form.signed_document.raw)

  const res = await axios.post(import.meta.env.VITE_APP_RC_RSA_ENDPOINT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

/**
 * Creates a new customer RSA version.
 *
 * @param {ICreateRSAVersionForm} form
 * @returns newly created customer RSA version.
 */
export async function createRSAVersion(form: ICreateRSAVersionForm) {
  const formData = new FormData()
  formData.append('rsa_number', form.rsa_number)
  if (form.start_date) {
    formData.append('start_date', DateTime.fromJSDate(form.start_date).startOf('day').toISO()!)
  }
  formData.append('months_term', form.months_term.toString())
  if (form.signed_document?.raw) formData.append('signed_document', form.signed_document.raw)

  const res = await axios.post(import.meta.env.VITE_APP_RC_RSA_VERSIONS_ENDPOINT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

/**
 * Searches resources.
 *
 * @param {IResourceSearchFilters} filters research source filters.
 */
export async function searchResources(filters?: IResourceSearchFilters) {
  const res = await axios.get(import.meta.env.VITE_APP_RC_SEARCH_RESOURCES_ENDPOINT, { params: filters })
  return res.data
}

/*
 * Add Bulk Containers.
 * @param {any} formData
 * @returns {Promise<any>} an array of containers.
 * @throws {AxiosError}
 *
 */
export const addBulkContainers = async (bulkContainersForm: IAddBulkContainersToShipmentForm) => {
  const generateFormData = (bulkContainersForm: IAddBulkContainersToShipmentForm) => {
    const formData = new FormData()

    formData.append('shipment_id', bulkContainersForm.shipment_id)

    bulkContainersForm.containers.forEach((container, index) => {
      // Container type is undefined while it's being defined in the UI
      // but it's defined after form validation checks out, prior to calling this function
      formData.append(`containers[${index}]type`, container.type!)
      formData.append(`containers[${index}]number`, container.number)
      formData.append(`containers[${index}]kind`, bulkContainersForm.shipment_kind)

      if (container.expected_miners)
        formData.append(`containers[${index}]expected_miners`, container.expected_miners.toString())

      if (container.length) formData.append(`containers[${index}]length`, container.length.toString())
      if (container.width) formData.append(`containers[${index}]width`, container.width.toString())
      if (container.height) formData.append(`containers[${index}]height`, container.height.toString())
      if (container.weight) formData.append(`containers[${index}]weight`, container.weight.toString())

      bulkContainersForm.damages.forEach((damage, damageIndex) => {
        if (damage.containerNumber === container.number) {
          formData.append(`containers[${index}]damages[${damageIndex}]description`, damage.description)
          formData.append(`containers[${index}]damages[${damageIndex}]picture`, damage.picture as File)
        }
      })
    })

    return formData
  }

  const res = await axios.post(
    `${import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT}/bulk`,
    generateFormData(bulkContainersForm),
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )

  return res.data
}

/**
 * Creates a container
 * @param {any} the container data
 * @throws {AxiosError}
 */
export const createContainer = async (container: IContainerCreationForm): Promise<IContainerDashboard> => {
  if (!container.work_order?.number) {
    throw new Error('Work order number is required')
  }

  const formData = new FormData()

  formData.append('work_order_number', container.work_order.number)
  formData.append('container_number', container.container_number)
  formData.append('type', container.container_type)
  formData.append('expected_miners', container.miners_expected.toString())
  formData.append('status', container.status)
  formData.append('kind', container.kind)

  // Add shipment
  if (container.shipment) {
    formData.append('shipment_id', container.shipment.toString())
  }
  if (container.shipment === null) {
    formData.append('shipment_id', '')
  }

  // Add miners
  if (container.miners) {
    if (isEmpty(container.miners)) {
      formData.append('miner_ids', '')
    } else {
      container.miners.forEach((miner, index) => {
        formData.append(`miner_ids[${index}]`, miner.id.toString())
      })
    }
  }

  // Created damages
  container.damages.forEach((damage, index) => {
    formData.append(`damages[${index}]description`, damage.description)
    formData.append(`damages[${index}]picture`, damage.picture as File)
  })

  // Add dimensions
  if (container.width) formData.append('width', container.width.toString())
  if (container.length) formData.append('length', container.length.toString())
  if (container.height) formData.append('height', container.height.toString())

  // Add weight
  if (container.weight) formData.append('weight', container.weight.toString())

  const res = await axios.post(import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return res.data
}

/*
 * Update Container.
 * @param {any} formData
 * @param {id} number
 * @returns {Promise<any>} an array of containers.
 * @throws {AxiosError}
 * @throws {APIResponseError}
 * @throws {APIRequestError}
 */
export const updateContainer = async (container: IContainerUpdateForm, id: number) => {
  const formData = new FormData()

  if (container.container_number) formData.append('container_number', container.container_number)
  if (container.container_type) formData.append('type', container.container_type)
  if (container.miners_expected) formData.append('expected_miners', container.miners_expected.toString())
  if (container.status) formData.append('status', container.status)

  if (container.shipment) {
    formData.append('shipment_id', container.shipment.toString())
  }
  if (container.shipment === null) {
    formData.append('shipment_id', '')
  }

  if (container.miner_ids) {
    if (isEmpty(container.miner_ids)) {
      formData.append('miner_ids', '')
    } else {
      container.miner_ids.forEach((minerID, index) => {
        formData.append(`miner_ids[${index}]`, minerID.toString())
      })
    }
  }

  if (container.newDamages) {
    container.newDamages.forEach((damage, index) => {
      formData.append(`new_damages[${index}]description`, damage.description)
      formData.append(`new_damages[${index}]picture`, damage.picture as File)
    })
  }

  if (container.deletedDamages) {
    container.deletedDamages.forEach((damage, index) => {
      formData.append(`deleted_damages[${index}]`, damage.id)
    })
  }

  // Add dimensions
  if (container.width) formData.append('width', container.width.toString())
  if (container.length) formData.append('length', container.length.toString())
  if (container.height) formData.append('height', container.height.toString())

  // Add weight
  if (container.weight) formData.append('weight', container.weight.toString())

  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT}/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return res.data
}

/**
 * Calls the update container endpoint with a single status update.
 *
 * @throws {AxiosError}
 */
export const updateContainerStatus = async (statusUpdate: IContainerStatusUpdate, containerId: number) => {
  const formData = updateContainerStatusFormData(statusUpdate)
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT}/${containerId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return res.data
}

/**
 * Delete a container by ID.
 *
 * @param id the container ID
 */
export async function deleteContainer(id: number) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Builds a container status update formData payload
 *
 * @param statusUpdate the status update, with single container 'status' key
 * @returns formData, post-able to the server via update container endpoint
 */
export const updateContainerStatusFormData = (statusUpdate: IContainerStatusUpdate) => {
  const formData = new FormData()
  formData.append('status', statusUpdate.status)
  return formData
}

/**
 * Gets all miner statuses.
 *
 * @returns {Promise<string[]>} an array of miner statuses.
 * @throws {AxiosError}
 */

export const getAllMinerStatuses = async (): Promise<string[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_API}/miner-status`)
  return res.data
}

/**
 * Gets all part statuses.
 *
 * @returns {Promise<string[]>} an array of part statuses.
 * @throws {AxiosError}
 */

export const getAllPartStatuses = async (): Promise<string[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_API}/part-status`)
  return res.data
}

/**
 * Get miner details
 *
 * @returns {Promise<IMinerDetails>} miner details.
 * @throws {AxiosError}
 */
export const getMinerDetails = async (id: number): Promise<IMinerDetails> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Creates a miner.
 *
 * @param createMinerForm miner to create.
 * @returns created miner.
 * @throws {AxiosError} Axios error for request or response.
 */
export const createMiner = async (createMinerForm: any) => {
  const formData = new FormData()
  Object.keys(createMinerForm).forEach((key) => {
    formData.append(`${key}`, createMinerForm[key])
  })

  const res = await axios.post(import.meta.env.VITE_APP_RC_MINERS_ENDPOINT, createMinerForm, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Deletes a single miner.
 *
 * @param minerId ID of miner to delete.
 * @returns deleted miner.
 */
export const deleteMiner = async (minerId: number) => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}`)
  return res.data
}

/**
 * Bulk deletes miners.
 *
 * @param {number[]} minerIds - The IDs of the miners to delete.
 * @returns {Promise<void>}
 * @throws {AxiosError}
 */
export const bulkDeleteMiners = async (minerIds: number[]) => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/bulk`, {
    data: { miner_ids: minerIds },
  })
  return res.data
}

/*
 * Download damages PDF.
 * @param {number} containerID
 * @returns {Promise<Blob>} a PDF blob.
 * @throws {Error}
 *
 */

export async function downloadDamagesPDF(containerID: number) {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT}/${containerID}/damages/pdf`, {
    responseType: 'blob',
  })
  return res.data
}

/**
 * Gets a miners page
 *
 * @param {number?} page
 * @param {number?} per_page
 * @returns {Promise<IMinerEndpointResponse>} an array of miners.
 * @throws {AxiosError}
 */
export async function getMiners(filters: IMinerDashboardFilters) {
  const res = await axios.get<IPage<IMinerDetails>>(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}`, {
    params: filters,
  })
  const minersPage = res.data
  return minersPage
}

/**
 * Update a miner's part (sn, model, etc...)
 *
 * @param {MinerUpdate} update
 * @returns {Promise<boolean>} ok status
 * @throws {AxiosError}
 */
export async function updateMinerParts(update: IMinerPartUpdateForm): Promise<IMinerDetailsLite> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${update.id}/parts`, update)
  return res.data
}

/**
 * Update miner
 *
 * @param {IMinerUpdateForm} data
 * @returns {Promise<IMinerDetails>} updated miner details
 * @throws {AxiosError}
 */
export const updateMiner = async (id: number, data: IMinerUpdateForm): Promise<IMinerDetailsLite> => {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}`, data)
  return res.data
}

/**
 * Update miner part status
 *
 * @param {IMinerPartRepairmentForm} data
 * @throws {AxiosError}
 */
export const updateMinerPartWithRepairmentData = async (
  minerId: number,
  partRepairmentForm: IMinerPartRepairmentForm
): Promise<IDetailsPart> => {
  const res = await axios.patch(
    `${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/parts/repairments`,
    partRepairmentForm
  )
  return res.data
}

/**
 * Update miner diagnostic
 *
 * @param {IMinerDiagnosticForm} minerDiagnosticForm: result and/or screenshot
 * @throws {AxiosError}
 */
export const updateMinerDiagnostic = async (
  minerId: number,
  minerDiagnosticForm: IMinerDiagnosticForm
): Promise<IMinerDetailsLite> => {
  const outboundFormData = new FormData()
  outboundFormData.append('date', minerDiagnosticForm.date.toISOString())
  if (minerDiagnosticForm.failure_reasons) {
    outboundFormData.append('failure_reasons', minerDiagnosticForm.failure_reasons)
  }
  if (minerDiagnosticForm.result) {
    outboundFormData.append('result', minerDiagnosticForm.result)
  }
  if (minerDiagnosticForm.files) {
    minerDiagnosticForm.files.forEach((uf, idx) => {
      // The following is checked in `RlUploadMultiple` and won't throw if `files` comes from it
      if (!uf.raw) throw new Error('A file being sent to the server is missing raw data')
      outboundFormData.append(`files[${idx}]`, uf.raw)
    })
  }
  const res = await axios.patch(
    `${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/diagnostic`,
    outboundFormData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return res.data
}

/**
 * Get miner part statuses
 *
 * @returns {Promise<string[]>} available part statuses
 * @throws {AxiosError}
 */
export const getMinerPartStatuses = async (): Promise<string[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_REPAIR_PART_STATUSES_ENDPOINT}`)
  return res.data
}

/**
 * Get miner statuses
 *
 * @returns {Promise<string[]>} available miner statuses
 * @throws {AxiosError}
 */
export const getMinerStatuses = async (): Promise<string[]> => {
  const res = await axios.get<string[]>(`${import.meta.env.VITE_APP_RC_REPAIR_MINER_STATUSES_ENDPOINT}`)
  return res.data
}

/**
 * Force miner status update
 *
 * @param {number} id: The miner ID
 * @param {{ status: string }} data: The miner status
 * @returns {Promise<IBasePart>} the updated miner's 'id', 'sn', 'model' and 'status'
 * @throws {AxiosError}
 */
export const forceMinerStatusUpdate = async (id: number, data: { status: string }): Promise<IMinerDetailsLite> => {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/status`, data)
  return res.data
}

/**
 * Create a stress test for a miner
 *
 * @param {number} id: The miner ID
 * @param {IStressTestForm} data: The stress test
 * @returns {Promise<IMinerDetailsLite>} the updated miner's details (lite)
 * @throws {AxiosError}
 */
export const createStressTest = async (id: number, data: IStressTestForm): Promise<IMinerDetailsLite> => {
  const outboundFormData = new FormData()
  outboundFormData.append('date', data.date?.toISOString() ?? new Date().toISOString())
  if (data.failure_reason) {
    outboundFormData.append('failure_reason', data.failure_reason)
  }
  if (data.result) {
    outboundFormData.append('result', data.result)
  }
  if (data.files) {
    data.files.forEach((uf, idx) => {
      // The following is checked in `RlUploadMultiple` and won't throw if `files` comes from it
      if (!uf.raw) throw new Error('A file being sent to the server is missing raw data')
      outboundFormData.append(`files[${idx}]`, uf.raw)
    })
  }
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/stress-tests`, outboundFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Retreieve all stress tests for a miner
 *
 * @param {number} id: The miner ID
 * @returns {Promise<IStressTest>} the updated miner's details (lite)
 * @throws {AxiosError}
 */
export const getStressTests = async (id: number): Promise<IStressTest[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/stress-tests`)
  return res.data
}

/**
 * Delete a miner's stress test
 *
 * @param {number} minerId: The miner ID
 * @param {number} stressTestId: The miner ID
 * @throws {AxiosError}
 */
export const deleteStressTests = async (minerId: number, stressTestIds: number[]) => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/stress-tests`, {
    data: { stress_test_ids: stressTestIds },
  })
  return res.data
}

/**
 * Get all failure types (for all parts)
 *
 * @returns {Promise<IFailureType[]>} all failure types
 */
export const getAllFailureTypes = async (): Promise<IFailureType[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_API}/failure-types`)
  return res.data
}

/**
 * Update a miner's part failure by id
 *
 * @param {number} failureId: The failure ID
 * @returns {IFailure} the updated failure
 * @throws {AxiosError}
 */
export const updateFailure = async (failureId: number, data: IUpdateFailureForm): Promise<IFailure> => {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_FAILURES_ENDPOINT}/${failureId}`, data)
  return res.data
}

/**
 * Delete a miner's part failure's repair action by failure id
 *
 * @param {number} failureId: The failure ID
 * @throws {AxiosError}
 */
export const deleteFailureRepairAction = async (failureId: number): Promise<IFailure> => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_FAILURES_ENDPOINT}/${failureId}/repair-action`)
  return res.data
}

/**
 * Clean miner pending failures.
 *
 * Edits a failure with a hardcoded executed_repair_action_id. Draft for parts repairs status updates. Will be removed eventually.
 *
 * @param {number} failureId: The failure ID
 * @throws {AxiosError}
 */
export const cleanupFailures = (failureId: number) =>
  updateFailure(failureId, {
    executed_repair_action_id: 9,
  })

/**
 * Adds pictures to a part's failure
 *
 * @param failureId the id of the failure to update
 * @param pictures the array of pictures
 */
export const addPartFailurePictures = async (failureId: number, pictures: File[]) => {
  const formData = new FormData()
  pictures.forEach((pic) => {
    formData.append('pictures', pic)
  })
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_FAILURES_ENDPOINT}/${failureId}/pictures`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Delete's a part's failure pictures
 *
 * @param failureId the id of the failure to clear pictures from
 */
export const deletePartFailurePictures = async (failureId: number, pictureIds: number[]) => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_FAILURES_ENDPOINT}/${failureId}/pictures`, {
    data: { picture_ids: pictureIds },
  })
  return res.data
}

/**
 * Get miner history
 *
 * @returns {Promise<IPage[IMinerHistory]>} history page
 * @throws {AxiosError}
 */
export const getMinerHistory = async (
  minerId: number,
  filters: IMinerHistoryFilters
): Promise<IPage<IMinerHistory>> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/history`, {
    params: filters,
  })
  return res.data
}

/**
 * Get spare part history
 *
 * @returns {Promise<IPage[ISparePartHistory]>} history page
 * @throws {AxiosError}
 */
export const getSparePartHistory = async (
  sparePartId: number,
  filters: ISparePartHistoryFilters
): Promise<IPage<ISparePartHistory>> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT}/${sparePartId}/history`, {
    params: filters,
  })
  return res.data
}

/**
 * Retrieves the bulk miners sample file
 */
export async function downloadSampleBulkMinersFile(): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_API}/miners-bulk-sample`, { responseType: 'blob' })
  return res.data
}

/**
 * Retrieves the bulk spare parts sample file
 */
export async function downloadSampleBulkSparePartsFile(): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_API}/spare-parts-bulk-sample`, { responseType: 'blob' })
  return res.data
}

/**
 * Create bulk miners
 *
 * @returns {Promise<BulkMinersResult>} bulk result
 * @throws {AxiosError}
 */
export async function createBulkMiners(workOrderNumber: string, file: File): Promise<BulkMinersResult> {
  const formData = new FormData()
  formData.append('work_order_number', workOrderNumber)
  formData.append('csv_file', file)

  const res = await axios.post(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/bulk`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return res.data
}

/**
 * Create bulk spare parts
 *
 * @returns {Promise<BulkSparePartsResult>} bulk result
 * @throws {AxiosError}
 */
export async function createBulkSpareParts(file: File): Promise<BulkSparePartsResult> {
  const formData = new FormData()
  formData.append('csv_file', file)

  const res = await axios.post(`${import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT}/bulk`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return res.data
}

/**
 * Get containers dashboard page
 *
 * @returns {Promise<IPage[IContainerDashboard]>} container dashboard page
 * @throws {AxiosError}
 */
export async function getContainers(filters: IContainerDashboardFilters): Promise<IPage<IContainerDashboard>> {
  const res = await axios.get(import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT, { params: filters })
  return res.data
}

/**
 * Gets a container's details.
 *
 * @param id container ID of container for which to fetch details.
 * @returns container details.
 */
export async function getContainerDetails(id: number): Promise<IContainer> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Gets paginated container miners by container number.
 *
 * @param id container ID.
 * @param filters container miner filters.
 * @returns paginated container miners.
 */
export async function getContainerMiners(id: number, filters: any): Promise<IPage<IContainerMiner>> {
  const res = await axios.get<IPage<IContainerMiner>>(
    `${import.meta.env.VITE_APP_RC_CONTAINERS_ENDPOINT}/${id}/miners`,
    {
      params: filters,
    }
  )
  return res.data
}

/**
 * Gets all container statuses.
 *
 * @returns {Promise<string[]>} an array of statuses.
 * @throws {AxiosError}
 */
export const getContainerStatuses = async (): Promise<string[]> => {
  const res = await axios.get(import.meta.env.VITE_APP_RC_CONTAINER_STATUSES_ENDPOINT)
  return res.data
}

/**
 * Gets all component types for a given model/version.
 *
 * @returns {Promise<IComponentType[]>} an array of component types.
 * @throws {AxiosError}
 */
export const getComponentTypes = async (part: PartType, model: string, version?: string): Promise<IComponentType[]> => {
  const res = await axios.get(import.meta.env.VITE_APP_RC_COMPONENT_TYPES_ENDPOINT, {
    params: { part, model, version },
  })
  return res.data
}

/**
 * Gets all components for a given model/component type.
 *
 * @returns {Promise<IComponent[]>} an array of available components.
 * @throws {AxiosError}
 */
export const getComponents = async (
  part: PartType,
  model: string,
  component_type_name: string,
  version?: string
): Promise<IComponent[]> => {
  const res = await axios.get(import.meta.env.VITE_APP_RC_COMPONENTS_ENDPOINT, {
    params: { part, model, version, component_type_name },
  })
  return res.data
}

/**
 * Adds replaced components to a miner's part.
 *
 * @returns {Promise<IDetailsPart>} the updated part
 * @throws {AxiosError}
 */
export const addReplacedComponents = async (
  minerId: number,
  part_id: number,
  part_type: PartType,
  components: IComponent[]
): Promise<IDetailsPart> => {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/components`, {
    part_id,
    part_type,
    components,
  })
  return res.data
}

/**
 * Deletes a replaced component from a miner's part.
 *
 * @returns {Promise<IDetailsPart>} the updated part
 * @throws {AxiosError}
 */
export const deleteReplacedComponent = async (
  minerId: number,
  part_id: number,
  part_type: PartType,
  component_id: number
): Promise<IDetailsPart> => {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/components`, {
    data: { part_id, part_type, component_id },
  })
  return res.data
}

/* Spare parts */

/**
 * Get spare parts dashboard page
 *
 * @returns {Promise<IPage[ISparePart]>} spare part dashboard page
 * @throws {AxiosError}
 */
export async function getSpareParts(filters: ISparePartsDashboardFilters): Promise<IPage<ISparePart>> {
  const params: { [key: string]: string } = Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined || (Array.isArray(value) && value.length > 0))
      .map(([key, value]) => (Array.isArray(value) ? [key, value.join(',')] : [key, value]))
  )

  const res = await axios.get(import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT, { params })
  return res.data
}

/**
 * Create a spare part.
 *
 * @param part spare part data
 * @throws {AxiosError}
 */
export async function createSparePart(part: ISparePartCreationForm): Promise<ISparePart> {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT}`, part)
  return res.data
}

/**
 * Update a spare part by id.
 *
 * @param id spare part id
 * @param partUpdate spare part update data
 */
export async function updateSparePart(id: number, partUpdate: ISparePartUpdateForm): Promise<ISparePart> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT}/${id}`, partUpdate)
  return res.data
}

/**
 * Update a collection of spare parts' location, owner and/or price
 *
 * @param spare_part_ids the spare parts ids
 * @param update the update form
 */
export async function bulkUpdateSpareParts(
  spare_part_ids: number[],
  updateForm: ISparePartBulkUpdateForm
): Promise<unknown> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT}/bulk`, {
    spare_part_ids,
    ...updateForm,
  })
  return res.data
}

/**
 * Delete a spare part by id.
 *
 * @param id spare part id
 */
export async function deleteSparePart(id: number) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Refurbish a spare part
 *
 * @param id spare part id
 */
export async function refurbishSparePart(id: number) {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_SPARE_PARTS_ENDPOINT}/${id}/refurbish`)
  return res.data
}

/**
 * Get invoices
 *
 * @returns {Promise<IPage[IInvoice]>} invoice page
 * @throws {AxiosError}
 */
export const getInvoices = async (filters: IInvoiceFilters): Promise<IPage<IInvoice>> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_INVOICES_ENDPOINT}`, {
    params: filters,
  })
  return res.data
}

/**
 * Gets invoice details.
 *
 * @param id invoice ID
 * @returns invoice details.
 */
export async function getInvoiceDetails(id: number): Promise<IInvoiceDetails> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_INVOICES_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Retrieves the Invoice CSV
 *
 * @param number invoice ID.
 */
export async function getInvoiceCSV(id: number): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_INVOICES_ENDPOINT}/${id}/csv`, {
    responseType: 'blob',
  })
  return res.data
}

/**
 * Delete an invoice
 *
 * @param invoiceID the invoice ID
 */
export async function deleteInvoice(id: number) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_INVOICES_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Refresh an invoice
 *
 * @param invoiceID the invoice ID
 */
export async function refreshInvoice(id: number) {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_INVOICES_ENDPOINT}/${id}/refresh`)
  return res.data
}

/**
 * Creates an invoice
 *
 * @param invoice the work_order it belongs to, along with the invoice's containers list
 */
export async function createInvoice(invoice: IInvoiceCreationForm) {
  const res = await axios.post(import.meta.env.VITE_APP_RC_INVOICES_ENDPOINT, invoice)
  return res.data
}

/**
 * Update an invoice
 *
 * @param invoice the work_order it belongs to, along with the invoice's containers list
 */
export async function updateInvoice(invoiceId: number, update: IInvoiceUpdateForm) {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_INVOICES_ENDPOINT}/${invoiceId}`, update)
  return res.data
}

/**
 * Refresh miner OEM expiration date
 *
 * @param id miner ID
 *
 */
export async function refreshMinerWarranty(id: number): Promise<IMinerIdentifier> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/warranty`)
  return res.data
}
